import { Injectable } from '@angular/core';
import { Observable, of, combineLatest } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { MenuItem } from '../../lib/sofbox/models/menu-item';
import AppMenuConfig from '../config/menu.config.json';
import MobileAppMenuConfig from '../config/mobile.app.menu.config.json';
import { AuthenticationService } from './authentication.service';
import { map, switchMap, catchError, shareReplay } from 'rxjs/operators';
import { CurrentUser, OrganisationUser } from '../models/auth-user';
import { LoggedInUser } from './managerial-operations.service';
import { environment } from 'src/environments/environment';
import { UserAuthService, IOrganisationTypeResp } from './user-auth.service';
import { MenuFocusService } from './menu-focus.service';
import { resolveOverviewLabel } from '../utils/custom-overview-label';
import { shouldHideAgentFields } from '../utils/agent-field-gating';
import { IManagerAccess, MANAGER_ACCESS_DEFAULT } from '../models/manager-access';

/** Report Settings on/off switches, as GET report-submissions/:org/kinds returns them. */
export interface IReportSwitches {
  client: boolean;
  dayEnd: boolean;
}

const REPORT_SWITCHES_ON: IReportSwitches = { client: true, dayEnd: true };
const REPORT_MENU_LINKS = ['/app/report-forms', '/app/report-submissions'];
// Reports → Work End Report (a manager's or employee's own report).
const OWN_WORK_END_LINK = '/app/work-end-report';

/** Everything outside role+group that decides whether a menu item is visible. */
interface MenuGateContext {
  isBiometricEnabled: boolean;
  isRestricted: boolean;
  isPayrollEnabled: boolean;
  isLiveStreamingEnabled: boolean;
  isMobileOrg: boolean;
  hideAgentFields: boolean;
  // Manager access switchboard. Only ever consulted for role 3 — an admin is never restricted
  // by it — so these stay `true` for everyone else and the gate below is a no-op for them.
  isManager: boolean;
  managerAccess: IManagerAccess;
}

@Injectable({
  providedIn: 'root'
})
export class MenuService {

  // Per-org enabled-groups response cached for the session so the menu build
  // and the route guard don't each hit the API on every navigation. Cleared on
  // reload/re-login, which is when menu-customization changes should take hold.
  private groupsCache = new Map<string, Observable<Record<string, boolean>>>();

  // Per-org module flags (isBiometricEnabled, defaultLandingPage, …). Cached for
  // the same reason as groupsCache — getOrgTypeDetails opens a tenant DB
  // connection server-side, and the menu build + landing resolver both need it.
  private orgTypeCache = new Map<string, Observable<IOrganisationTypeResp | null>>();

  // Manager access switchboard, cached per org for the session like the two above. A failed read
  // falls back to permissive: a settings endpoint that is down must not lock managers out of the
  // app, and the API enforces the one flag that guards a write regardless.
  private managerAccessCache = new Map<string, Observable<IManagerAccess>>();

  // Report Settings switches (Client Reports / Work End Report), cached per org for
  // the session. Both off hides Report forms + Report submissions; Work End off
  // hides the Employee Reports "Work End Report" tab. Saving Report Settings
  // clears it (clearReportSwitches) and reloads the page when the menu changes.
  private reportSwitchesCache = new Map<string, Observable<IReportSwitches>>();

  // Whether the signed-in employee / manager / promoted admin has a Work End form to submit
  // (feature on + designation + active form), cached for the session.
  private ownWorkEndCache = new Map<string, Observable<boolean>>();

  constructor(
    private authService: AuthenticationService,
    private http: HttpClient,
    private userAuthService: UserAuthService,
    private menuFocus: MenuFocusService,
  ) { }

  // Landing URL after login. With menu customization a user's first visible
  // menu (e.g. Dashboard) can be hidden, so navigating to a fixed '/app' lands
  // them on a page with no matching sidebar entry. Resolve the first VISIBLE
  // menu link instead, descending into sections whose parent has no own link.
  // Falls back to '/app' when the list is empty or the lookup fails.
  // An org can pin its own landing menu (organisation.default_landing_page, e.g.
  // '/app/analysis/employee-work-status'). It is honoured ONLY when that link is
  // actually visible in this user's filtered menu — otherwise (unset, hidden for
  // the role, group disabled, module gated off, stale/renamed link, or the
  // org-type call failing to null) we fall back to the first visible menu, so a
  // configured landing can never strand someone on a page they can't see.
  getFirstMenuLink(): Observable<string> {
    return combineLatest([
      this.getMenuList(),
      this.authService.getCurretUser().pipe(
        switchMap((userInfo) => this.getOrgType(this.extractOrgCode(userInfo))),
      ),
    ]).pipe(
      map(([items, orgType]) => {
        // NOTE: no optional chaining (`?.`) anywhere in this file — tsconfig.server.json
        // targets ES2020, so TS emits `?.` as-is and the SSR webpack/babel pipeline
        // fails to parse it. Use explicit null checks instead.
        const configured = orgType ? orgType.defaultLandingPage : null;
        if (configured && this.linkExistsInMenu(configured, items)) {
          return configured;
        }
        // Empty menu = every group this user's role can see is disabled → there is
        // no page to land on, so send them to the no-access page. On an unexpected
        // error fall back to '/app' (the RoleGuard still routes them correctly).
        return this.findFirstLink(items) || '/no-access';
      }),
      catchError(() => of('/app')),
    );
  }

  // Is `link` a reachable leaf in the already-filtered menu? Mirrors
  // findFirstLink's traversal (skip headings, descend into sections) so the two
  // always agree on what "visible" means.
  private linkExistsInMenu(link: string, items: MenuItem[]): boolean {
    for (const item of items || []) {
      if ((item as any).isHeading) {
        continue;
      }
      if (item.children && item.children.length) {
        if (this.linkExistsInMenu(link, item.children)) {
          return true;
        }
        continue;
      }
      if (item.link === link) {
        return true;
      }
    }
    return false;
  }

  private findFirstLink(items: MenuItem[]): string | null {
    if (!items || !items.length) {
      return null;
    }
    for (const item of items) {
      if ((item as any).isHeading) {
        continue;
      }
      if (item.children && item.children.length) {
        const childLink = this.findFirstLink(item.children);
        if (childLink) {
          return childLink;
        }
        continue;
      }
      if (item.link) {
        return item.link;
      }
    }
    return null;
  }

  // Route-level enforcement for menu customization. A hidden menu (its group is
  // disabled for the org) must not be reachable by typing its URL. Maps the URL
  // to the menu item that owns it (exact link, else the deepest link prefix) and
  // returns whether that item's group is enabled. Routes not owned by any menu
  // item (detail/sub pages with no menu entry, ungrouped areas) are allowed, and
  // any error fails OPEN so a config/API blip never locks users out.
  canAccessRoute(url: string): Observable<boolean> {
    return this.authService.getCurretUser().pipe(
      switchMap((userInfo) => {
        let orgCode = '';
        if (userInfo instanceof OrganisationUser) {
          orgCode = userInfo.organisationCode || '';
        } else if (userInfo instanceof LoggedInUser) {
          orgCode = (userInfo as any).organisationCode || '';
        } else if (userInfo instanceof CurrentUser) {
          orgCode = (userInfo as any).organisationCode || '';
        }
        const isRestricted = !!(userInfo && (userInfo as any).isRestricted);
        const roleId = this.extractRoleId(userInfo);
        return combineLatest([
          this.getEnabledGroups(orgCode),
          this.getOrgType(orgCode),
          this.getManagerAccess(orgCode, roleId === 3),
        ]).pipe(
          map(([enabledGroups, orgType, managerAccess]) =>
            this.isUrlAllowed(
              url,
              enabledGroups,
              this.buildGateContext(orgType, isRestricted, roleId === 3, managerAccess),
            ),
          ),
        );
      }),
      catchError(() => of(true)),
    );
  }

  // Same predicate the menu build uses (groupAllowed + moduleAllowed) applied
  // at the route level, so a module hidden from the sidebar (e.g. a CR-012
  // agent-only Settings page) can't be reached by typing its URL either.
  private isUrlAllowed(
    url: string,
    enabledGroups: Record<string, boolean>,
    ctx: MenuGateContext,
  ): boolean {
    const clean = (url || '').split('?')[0].split('#')[0];
    const links = this.flattenLinks(AppMenuConfig.data as Array<any>);
    // Exact link match first (handles the '/app' dashboard and leaf menu links).
    let match = links.find((l) => l.link === clean);
    if (!match) {
      // Otherwise the deepest link that is a path-prefix of the URL owns it
      // (e.g. a detail page under a section). The bare '/app' root is
      // exact-match only — it prefixes every route and must not swallow
      // unrelated pages.
      let best: { link: string; groups: string[]; title: string } | undefined;
      for (const l of links) {
        if (l.link === '/app') {
          continue;
        }
        if (clean === l.link || clean.startsWith(l.link + '/')) {
          if (!best || l.link.length > best.link.length) {
            best = l;
          }
        }
      }
      match = best;
    }
    // No owning menu item → not a customizable route → allow.
    if (!match) {
      return true;
    }
    return this.groupAllowed(match, enabledGroups) && this.moduleAllowed(match, ctx);
  }

  // Flatten menu config to {link, groups, title} entries, propagating a
  // parent's groups to children that declare none — a child is hidden when
  // its OWN group or its section's group is disabled, mirroring buildMenu()'s
  // section pruning. `title` is carried per-item (never inherited) so
  // moduleAllowed() can apply the same title-keyed checks it uses for the
  // sidebar build.
  private flattenLinks(
    items: Array<any>,
    parentGroups?: string[],
  ): Array<{ link: string; groups: string[]; title: string }> {
    const out: Array<{ link: string; groups: string[]; title: string }> = [];
    for (const item of items) {
      const groups =
        (item.groups && item.groups.length) ? item.groups : parentGroups;
      if (item.link) {
        out.push({ link: item.link, groups, title: item.title });
      }
      if (item.children && item.children.length) {
        out.push(...this.flattenLinks(item.children, groups));
      }
    }
    return out;
  }

  getMenuList(): Observable<MenuItem[]> {
    return this.authService.getCurretUser().pipe(
      switchMap((userInfo) => {
        const orgCode = this.extractOrgCode(userInfo);
        const roleId = this.extractRoleId(userInfo);
        // Restricted-access is a property of the USER (not the org).
        const isRestricted = !!(userInfo && (userInfo as any).isRestricted);

        // Pull the org's enabled menu groups + module flags, then filter by
        // role + group + module gating in ONE place (see buildMenu).
        return combineLatest([
          this.getEnabledGroups(orgCode),
          this.getOrgType(orgCode),
          this.getManagerAccess(orgCode, roleId === 3),
          this.getReportSwitches(orgCode, roleId),
          this.getOwnWorkEnd(orgCode, roleId),
        ]).pipe(
          map(([enabledGroups, orgType, managerAccess, reportSwitches, ownWorkEnd]) =>
            this.withOverviewShortcut(
              this.withoutOffReports(
                this.buildMenu(
                  roleId,
                  enabledGroups,
                  this.buildGateContext(orgType, isRestricted, roleId === 3, managerAccess),
                ),
                reportSwitches,
                ownWorkEnd,
              ),
              orgType,
            ),
          ),
        );
      })
    );
  }

  // The org's pinned landing (organisation.default_landing_page) is surfaced as
  // the FIRST sidebar entry, so the page an org starts on is one click away and
  // visibly "where this org begins". The page's own in-section entry is removed
  // (not just re-labelled) so the same route never appears twice in the
  // sidebar — the Overview row is the only way to reach it once pinned.
  // Injected ONLY when the pinned link is already visible in this user's filtered
  // menu — the same rule getFirstMenuLink() applies — so it can never surface a
  // page the sidebar hides (role, group or module gated). Skipped when the pinned
  // link is already the first entry: an Overview row pointing at Dashboard would
  // just duplicate the row beneath it.
  private withOverviewShortcut(
    items: MenuItem[],
    orgType: IOrganisationTypeResp | null,
  ): MenuItem[] {
    // No optional chaining (`?.`) in this file — see the note in getFirstMenuLink().
    const configured = orgType ? orgType.defaultLandingPage : null;
    if (!configured || !this.linkExistsInMenu(configured, items)) {
      this.menuFocus.setOverviewLink(null);
      return items;
    }
    if (this.findFirstLink(items) === configured) {
      this.menuFocus.setOverviewLink(null);
      return items;
    }
    // Still needed even though the in-section twin is removed below: the nav
    // rail (rail-menu.utils.ts flattenForRail) independently reads
    // overviewLink to pull items[0] out into its own "landing" slot — that
    // extraction is unconditional on whether a duplicate exists underneath.
    this.menuFocus.setOverviewLink(configured);
    // `children` is deliberately OMITTED (not []): the sidebar template
    // (list-style1.component.html) branches on the TRUTHINESS of `children`,
    // and an empty array is truthy — it would render this as an expandable
    // section (arrow + empty submenu) whose anchor carries no routerLink, so
    // the tab would not navigate at all. Leaving it undefined renders the
    // plain leaf link. Overview always points at exactly one route.
    const overview = {
      title: resolveOverviewLabel(orgType ? orgType.customOverviewLabel : null),
      isHeading: false,
      isActive: true,
      link: configured,
      className: '',
      isIconClass: true,
      icon: 'ri-layout-grid-line',
      roles: [],
      // `children` deliberately omitted (not []) — see the comment above.
      // `MenuItem.children` is a required field, so this object never
      // structurally satisfies MenuItem; route through `unknown` rather
      // than widen the shared interface (children is read unguarded at
      // several other call sites).
    } as unknown as MenuItem;
    return [overview, ...this.removeLink(items, configured)];
  }

  // Drops the leaf whose link matches `link` from the (already role/group/module
  // filtered) menu tree, so a page pinned as the landing shortcut is not also
  // left behind in its own section. Mirrors buildMenu()'s section pruning: a
  // parent container left with no children after the removal is dropped too,
  // same as when every child is gated off. Returns shallow clones; the input
  // is never mutated.
  private removeLink(items: MenuItem[], link: string): MenuItem[] {
    const result: MenuItem[] = [];
    for (const item of items) {
      if (item.children && item.children.length) {
        const children = this.removeLink(item.children, link);
        if (!children.length) {
          continue;
        }
        result.push({ ...item, children } as MenuItem);
        continue;
      }
      if (item.link === link) {
        continue;
      }
      result.push(item);
    }
    return result;
  }

  // Mirrors the entitlement conditions the sidebar applies (index.component:320-339).
  // A missing/failed org-type degrades a gated module to "hidden", which is the same
  // thing the sidebar shows — so the landing check and the sidebar stay in agreement.
  private buildGateContext(
    orgType: IOrganisationTypeResp | null,
    isRestricted: boolean,
    isManager = false,
    managerAccess: IManagerAccess = MANAGER_ACCESS_DEFAULT,
  ): MenuGateContext {
    // `|| {}` instead of optional chaining — see the note in getFirstMenuLink().
    const t: any = orgType || {};
    return {
      isManager,
      managerAccess,
      isBiometricEnabled: !!t.isBiometricEnabled,
      isRestricted,
      isPayrollEnabled: !!t.isPayroll && !!t.allowPayrollOption,
      isLiveStreamingEnabled: !!t.liveStreamingModule,
      isMobileOrg: t.isMobileOrg !== 0,
      hideAgentFields: shouldHideAgentFields(t.noMonitoringAgentModule),
    };
  }

  private extractOrgCode(userInfo: any): string {
    if (userInfo instanceof OrganisationUser) {
      return userInfo.organisationCode || '';
    }
    if (userInfo instanceof LoggedInUser || userInfo instanceof CurrentUser) {
      return (userInfo as any).organisationCode || '';
    }
    return '';
  }

  private extractRoleId(userInfo: any): number {
    if (userInfo instanceof CurrentUser) {
      return userInfo.UserDetails.roleId;
    }
    if (userInfo instanceof LoggedInUser || userInfo instanceof OrganisationUser) {
      return (userInfo as any).roleId;
    }
    return 0;
  }

  // Per-org module flags (isBiometricEnabled, defaultLandingPage, …). DEFAULT-SAFE
  // on the landing side: any error → null → no configured landing → first-visible
  // fallback. Reuses UserAuthService so the org-type call isn't duplicated here.
  /**
   * The manager switchboard, fetched ONLY for a manager.
   *
   * Nobody else is affected by it — `moduleAllowed` consults these flags behind `ctx.isManager`,
   * and an admin is never restricted by them — so for every other role this returns the permissive
   * default without an HTTP call at all.
   *
   * That is not an optimisation. The endpoint is admin/manager only, and the global error
   * interceptor turns ANY unhandled 403 into a redirect to /403, so calling it for an EMPLOYEE
   * threw every employee off the dashboard the moment they signed in. A `catchError` here cannot
   * help: the interceptor fires at the HTTP layer, before this pipe ever sees the failure.
   */
  private getManagerAccess(orgCode: string, isManager: boolean): Observable<IManagerAccess> {
    if (!orgCode || !isManager) {
      return of({ ...MANAGER_ACCESS_DEFAULT });
    }
    const cached = this.managerAccessCache.get(orgCode);
    if (cached) {
      return cached;
    }
    const request = this.userAuthService.getManagerAccess(orgCode).pipe(
      map((res: any) => ({ ...MANAGER_ACCESS_DEFAULT, ...(res && res.data ? res.data : {}) })),
      catchError(() => of({ ...MANAGER_ACCESS_DEFAULT })),
      shareReplay(1),
    );
    this.managerAccessCache.set(orgCode, request);
    return request;
  }

  /**
   * Report Settings switches, fetched ONLY for an admin or a manager: the endpoint
   * is theirs alone, and the global interceptor turns an employee's 403 into a
   * redirect (see getManagerAccess). Everyone else, and any failure, gets "on" —
   * a settings read that fails must never hide reports.
   */
  private getReportSwitches(orgCode: string, roleId: number): Observable<IReportSwitches> {
    if (!orgCode || (roleId !== 2 && roleId !== 3)) {
      return of({ ...REPORT_SWITCHES_ON });
    }
    const cached = this.reportSwitchesCache.get(orgCode);
    if (cached) {
      return cached;
    }
    const request = this.http
      .get<any>(`${environment.apiEndpoint}/api/v1/report-submissions/${orgCode}/kinds`)
      .pipe(
        map((res: any) => ({ ...REPORT_SWITCHES_ON, ...(res && res.data ? res.data : {}) })),
        catchError(() => of({ ...REPORT_SWITCHES_ON })),
        shareReplay(1),
      );
    this.reportSwitchesCache.set(orgCode, request);
    return request;
  }

  /** The signed-in user's Report Settings switches (cached; see getReportSwitches). */
  public getCurrentReportSwitches(): Observable<IReportSwitches> {
    return this.authService.getCurretUser().pipe(
      switchMap((userInfo) => this.getReportSwitches(this.extractOrgCode(userInfo), this.extractRoleId(userInfo))),
    );
  }

  /** Forget the cached switches, after Report Settings is saved. */
  public clearReportSwitches(): void {
    this.reportSwitchesCache.clear();
    this.ownWorkEndCache.clear();
  }

  // Both report kinds off: no Report forms / Report submissions entry at all.
  // No Work End form for this manager / employee: no Work End Report entry.
  private withoutOffReports(items: MenuItem[], switches: IReportSwitches, ownWorkEnd: boolean): MenuItem[] {
    let result = items;
    if (!switches.client && !switches.dayEnd) {
      for (const link of REPORT_MENU_LINKS) {
        result = this.removeLink(result, link);
      }
    }
    if (!ownWorkEnd) {
      result = this.removeLink(result, OWN_WORK_END_LINK);
    }
    return result;
  }

  /**
   * Whether this login has a Work End form (their own endpoint, so no 403): an
   * employee, a manager, or a promoted admin. An admin who is not an employee
   * gets null, so no entry. A failed read shows the entry — the page itself says
   * when there is no form.
   */
  private getOwnWorkEnd(orgCode: string, roleId: number): Observable<boolean> {
    if (!orgCode || (roleId !== 2 && roleId !== 3 && roleId !== 4)) {
      return of(false);
    }
    const cached = this.ownWorkEndCache.get(orgCode);
    if (cached) {
      return cached;
    }
    const request = this.http
      .get<any>(`${environment.apiEndpoint}/api/v1/my-reports/${orgCode}/work-end/form`)
      .pipe(
        map((res: any) => !!(res && res.data)),
        catchError(() => of(true)),
        shareReplay(1),
      );
    this.ownWorkEndCache.set(orgCode, request);
    return request;
  }

  /**
   * get-org-type for an org, fetched once and shared. Public so a page that
   * needs one field of it (the Salary tab wants organisation.name) reads the
   * copy the menu already paid for instead of issuing its own request.
   */
  public getOrgType(orgCode: string): Observable<IOrganisationTypeResp | null> {
    if (!orgCode) {
      return of(null);
    }
    const cached = this.orgTypeCache.get(orgCode);
    if (cached) {
      return cached;
    }
    const request = this.userAuthService.getOrgTypeDetails(orgCode).pipe(
      map((res: any) => (res && res.data) ? res.data as IOrganisationTypeResp : null),
      catchError(() => of(null)),
      shareReplay(1),
    );
    this.orgTypeCache.set(orgCode, request);
    return request;
  }

  // Per-org menu-group enablement (authored by Super Admin). DEFAULT-SAFE: on a
  // missing orgCode or ANY error the CALLER still gets {} (the filter treats
  // that as "everything enabled" — the sidebar is never blanked by one config
  // failure). F-252: a failed fetch must NOT be cached as if it were a
  // genuine "org has no restrictions" response — that indistinguishably and
  // permanently un-hides every disabled group for the rest of the session.
  // So only a fetch that actually resolved (even to a legitimate empty {})
  // is stored in groupsCache; an error is caught OUTSIDE the cached
  // shareReplay pipe and evicts the entry so the next caller retries instead
  // of replaying the same failure forever.
  private getEnabledGroups(orgCode: string): Observable<Record<string, boolean>> {
    if (!orgCode) {
      return of({} as Record<string, boolean>);
    }
    let request = this.groupsCache.get(orgCode);
    if (!request) {
      const url =
        `${environment.apiEndpoint}/api/v1/organisation-management/organisations/${orgCode}/menu-groups`;
      request = this.http
        .get<{ data: { groups: Record<string, boolean> } }>(url)
        .pipe(
          map((res) => (res && res.data && res.data.groups) ? res.data.groups : {}),
          shareReplay(1),
        );
      this.groupsCache.set(orgCode, request);
    }
    const inFlight = request;
    return inFlight.pipe(
      catchError(() => {
        // Only evict if nobody else has already replaced this entry with a
        // fresh retry (avoids a straggler error handler deleting a newer,
        // still-good cache entry).
        if (this.groupsCache.get(orgCode) === inFlight) {
          this.groupsCache.delete(orgCode);
        }
        return of({} as Record<string, boolean>);
      }),
    );
  }

  // Direct check on a single named group (e.g. 'monitoring'), for callers that
  // gate on a specific known group rather than resolving a menu/route — same
  // enabled/disabled semantics as groupAllowed: a key missing from the stored
  // map defaults to enabled, so a config/API failure never hides anything.
  isGroupEnabled(orgCode: string, groupKey: string): Observable<boolean> {
    return this.getEnabledGroups(orgCode).pipe(
      map((enabledGroups) => enabledGroups[groupKey] !== false),
    );
  }

  // A menu is group-allowed when it has no `groups` (base/ungrouped → always
  // visible) OR at least one of its groups is enabled. A group missing from the
  // map defaults to enabled (treated as not-false).
  private groupAllowed(
    item: any,
    enabledGroups: Record<string, boolean>,
  ): boolean {
    if (!item.groups || !item.groups.length) {
      return true;
    }
    return item.groups.some((g: string) => enabledGroups[g] !== false);
  }

  // Module / access gating that used to live in the sidebar component
  // (index.component.ts) and was therefore INVISIBLE to the landing resolver and
  // the guards — so a landing pinned to a biometric-only menu passed validation
  // and dropped the user on a page missing from their sidebar. Centralised here
  // so the sidebar, getFirstMenuLink() and the guards share one definition of
  // "visible" and can never drift apart again.
  private moduleAllowed(item: any, ctx: MenuGateContext): boolean {
    const title = (item.title || '').toLowerCase();
    // Biometric is an org module: its own group disappears when it's off.
    //
    // Employee Work Status used to be dropped here too. It no longer is: the
    // page mirrors the attendance report, serves every org, and hides its two
    // device-only columns itself. The biometric GROUP still goes — those pages
    // administer devices an org without devices does not have.
    if (!ctx.isBiometricEnabled) {
      if (title === 'menu.biometric') {
        return false;
      }
    }
    // Restricted-access users don't get Settings.
    if (ctx.isRestricted && title === 'menu.settings') {
      return false;
    }
    // Payroll needs BOTH the entitlement and the org opt-in (index.component:326).
    if (!ctx.isPayrollEnabled && title === 'menu.payroll') {
      return false;
    }
    // CR-007/CR-008 — Loans & Advances is gated by the SAME existing Payroll
    // entitlement (isPayrollEnabled), not a new commercial flag — recovery
    // deductions fold into payroll, so a tenant without Payroll has nothing
    // for this module to hang off of.
    if (!ctx.isPayrollEnabled && title === 'menu.loans_advances') {
      return false;
    }
    if (!ctx.isLiveStreamingEnabled && title === 'menu.live_streaming') {
      return false;
    }
    // Non-mobile orgs don't get Site / Location (index.component:320).
    if (!ctx.isMobileOrg && (title === 'menu.site' || title === 'menu.location')) {
      return false;
    }
    // CR-012 (Settings Simplification): these 4 Settings children only
    // configure or report on desktop/mobile-agent activity, so they reuse the
    // D-074 no-monitoring-agent flag rather than new schema.
    if (ctx.hideAgentFields) {
      const agentOnlySettingsTitles = ['menu.general', 'menu.emaildigest', 'menu.activity', 'menu.activityset'];
      if (agentOnlySettingsTitles.includes(title)) {
        return false;
      }
    }
    // Manager access switchboard: the admin decides, per org, which of these a manager sees.
    // Only role 3 is ever affected — an admin's menu is untouched by these flags.
    if (ctx.isManager) {
      if (!ctx.managerAccess.leavePermission && title === 'menu.leave_and_permission') {
        return false;
      }
      if (!ctx.managerAccess.employees && title === 'menu.employees') {
        return false;
      }
      // Biometric OT is the one switch that GRANTS rather than removes: it is off until an admin
      // turns it on, so a manager sees this report only where that has happened. The org-level
      // biometric check above already hides it from non-biometric orgs entirely.
      if (!ctx.managerAccess.biometricOt && title === 'biometric ot') {
        return false;
      }
      // The common Overtime report is gated by the SAME switch (its API uses the 'biometricOt'
      // manager-access flag), but it is open to every tenant, so there is no org-level check
      // above it: a manager sees it exactly when the admin turned the switch on.
      if (!ctx.managerAccess.biometricOt && title === 'overtimemenu.report') {
        return false;
      }
      // The Biometric SECTION had to open to role 3 for the OT report to be reachable at all —
      // buildMenu() applies `roles` to top-level items only, and filters children through this
      // predicate alone. So its admin-only siblings have to be refused here, or opening the
      // section would hand managers the device registry and the biometric settings page too.
      // Same predicate backs canAccessRoute, so typing either URL is refused as well.
      if (title === 'menu.biometric_devices' || title === 'menu.biometric_settings') {
        return false;
      }
    }
    return true;
  }

  // SIDEBAR-ONLY visibility, deliberately separate from moduleAllowed(): moduleAllowed also backs
  // canAccessRoute(), i.e. it decides whether a typed URL opens, and these two rules must hide a
  // LINK without closing the PAGE.
  //   hideFromMenu    - the entry is retired from the menu but its routes keep working
  //                     (Biometric OT, replaced by the common Overtime report).
  //   hideWhenNoAgent - the entry only makes sense where monitoring agents run, so a tenant with
  //                     no agents does not see it; the URL still opens (a link from another page
  //                     or a bookmark must keep working). Reads the same D-074 flag as every
  //                     other agent-only hide (shouldHideAgentFields): fail-open, so a failed or
  //                     missing org-type shows the entry rather than hiding it.
  private menuEntryAllowed(item: any, ctx: MenuGateContext): boolean {
    if (item.hideFromMenu) {
      return false;
    }
    if (item.hideWhenNoAgent && ctx.hideAgentFields) {
      return false;
    }
    return true;
  }

  // Role filter (unchanged) + group filter + module gating. For sections, hidden
  // children are pruned and a parent whose children all hide is dropped. Returns
  // shallow clones so the imported config JSON is never mutated.
  private buildMenu(
    roleId: number,
    enabledGroups: Record<string, boolean>,
    ctx: MenuGateContext,
  ): MenuItem[] {
    const configData = AppMenuConfig.data as Array<any>;
    const result: MenuItem[] = [];

    for (const item of configData) {
      if (!item.roles.includes(roleId)) {
        continue;
      }
      if (!this.groupAllowed(item, enabledGroups) || !this.moduleAllowed(item, ctx)) {
        continue;
      }

      if (item.children && item.children.length) {
        const visibleChildren = item.children.filter(
          (child: any) =>
            this.groupAllowed(child, enabledGroups) &&
            this.moduleAllowed(child, ctx) &&
            this.menuEntryAllowed(child, ctx),
        );
        // A container whose children are all hidden adds nothing — drop it.
        if (!visibleChildren.length) {
          continue;
        }
        result.push({ ...item, children: visibleChildren } as MenuItem);
      } else {
        result.push(item as MenuItem);
      }
    }

    return result;
  }
}
